import { TaskStatus } from "@/types/tasks";
import { GoalStatus } from "@/types/goals";
import { OKT } from "@/services/api";
import { COMPLETED_TASK_STATUSES, ACTIVE_TASK_STATUSES } from "@/constants/goals";

/**
 * Status color map for tasks (OKTs)
 */
export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  IN_PROGRESS: "bg-primary-100 text-primary-800",
  COMPLETED: "bg-success-100 text-success-800",
  NOT_STARTED: "bg-neutral-100 text-neutral-800",
  NOT_REQUIRED: "bg-warning-100 text-warning-800",
  MISSED: "bg-error-100 text-error-800",
};

/**
 * Status label map for tasks (OKTs)
 */
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  IN_PROGRESS: "In progress",
  COMPLETED: "Completed",
  NOT_STARTED: "Not started",
  NOT_REQUIRED: "Not required",
  MISSED: "Missed",
};

/**
 * Status color map for goals (OKRs)
 */
export const GOAL_STATUS_COLORS: Record<GoalStatus, string> = {
  ACTIVE: "bg-primary-100 text-primary-800",
  ARCHIVED: "bg-neutral-100 text-neutral-800",
  DROPPED: "bg-error-100 text-error-800",
  NOT_STARTED: "bg-neutral-100 text-neutral-800",
  IN_PROGRESS: "bg-primary-100 text-primary-800",
  MISSED: "bg-error-100 text-error-800",
  ACHIEVED: "bg-success-100 text-success-800",
};

/**
 * Status label map for goals (OKRs)
 */
export const GOAL_STATUS_LABELS: Record<GoalStatus, string> = {
  ACTIVE: "Active",
  ARCHIVED: "Archived",
  DROPPED: "Dropped",
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  MISSED: "Missed",
  ACHIEVED: "Achieved",
};

/**
 * Get status color for a task
 */
export const getTaskStatusColor = (status: string): string => {
  return TASK_STATUS_COLORS[status as TaskStatus] || "bg-neutral-100 text-neutral-800";
};

/**
 * Get status label for a task
 */
export const getTaskStatusLabel = (status: string): string => {
  return TASK_STATUS_LABELS[status as TaskStatus] || status;
};

/**
 * Get status color for a goal
 */
export const getGoalStatusColor = (status: string): string => {
  return GOAL_STATUS_COLORS[status as GoalStatus] || "bg-neutral-100 text-neutral-800";
};

/**
 * Get status label for a goal
 */
export const getGoalStatusLabel = (status: string): string => {
  return GOAL_STATUS_LABELS[status as GoalStatus] || status;
};

/**
 * Calculate progress percentage based on resolved tasks
 * Uses COMPLETED_TASK_STATUSES (COMPLETED, NOT_REQUIRED, MISSED) — same as backend
 */
export const calculateProgress = (okts: OKT[]): number => {
  if (!okts || okts.length === 0) return 0;
  const completedCount = okts.filter(
    (okt) => COMPLETED_TASK_STATUSES.includes(okt.status as typeof COMPLETED_TASK_STATUSES[number]),
  ).length;
  return Math.round((completedCount / okts.length) * 100);
};

/**
 * Determine OKR status based on task statuses
 */
export const determineOKRStatus = (tasks: OKT[]): GoalStatus => {
  // If any task is in progress or not started, goal should be active
  if (tasks.some((task) => ACTIVE_TASK_STATUSES.includes(task.status))) {
    return "ACTIVE";
  }

  // If all tasks are in completed statuses, goal should be achieved
  if (tasks.length > 0 && tasks.every((task) => COMPLETED_TASK_STATUSES.includes(task.status))) {
    return "ACHIEVED";
  }

  // Default to active
  return "ACTIVE";
};

/**
 * Check if a task is completed/resolved (for display purposes)
 */
export const isTaskCompleted = (status: string): boolean => {
  return COMPLETED_TASK_STATUSES.includes(status as typeof COMPLETED_TASK_STATUSES[number]);
};

/**
 * Check if a goal is completed/achieved
 */
export const isGoalCompleted = (status: string): boolean => {
  return status === "ACHIEVED";
};

export interface ParsedGoalDescription {
  objective?: string;
  expectedResult?: string;
  /** Set when the description has no Objective/Expected Result/Result labels — render as-is */
  plainText?: string;
}

// Matches a label at the start of a line, e.g. "Objective:" or "Expected Result:".
// "Result" alone is treated as a synonym for "Expected Result" (legacy single-field goals).
const GOAL_DESCRIPTION_LABEL_PATTERN = /^[ \t]*(Objective|Expected Result|Result)[ \t]*:[ \t]*/gim;

/**
 * Split a goal's free-text description into its Objective / Expected Result
 * sections when the author labeled them, preserving line breaks within each
 * section. Falls back to `plainText` (the description verbatim) when no
 * labels are present.
 */
export const parseGoalDescription = (description: string | null | undefined): ParsedGoalDescription => {
  if (!description) return {};

  const matches = [...description.matchAll(GOAL_DESCRIPTION_LABEL_PATTERN)];
  if (matches.length === 0) {
    return { plainText: description };
  }

  const parsed: ParsedGoalDescription = {};
  matches.forEach((match, i) => {
    const label = match[1].toLowerCase();
    const start = match.index! + match[0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index! : description.length;
    const content = description.slice(start, end).trim();
    if (!content) return;
    if (label === "objective") {
      parsed.objective = content;
    } else {
      // "expected result" and "result" both map to the same field
      parsed.expectedResult = content;
    }
  });

  return parsed;
};
